// frontend/src/screens/admin/AdminPickingSlipScreen.jsx

import React, { useCallback, useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  ArrowLeft,
  CheckSquare,
  Loader2,
  PackageCheck,
  Printer,
  RefreshCw,
  Scissors,
} from "lucide-react";
import { toast } from "react-toastify";

import {
  getAdminOrderByNumber,
  resetAdminOrderError,
  resetAdminOrderMeta,
} from "../../../actions/adminOrderActions";

import Loader from "../../../components/Loader";
import Message from "../../../components/Message";
import Meta from "../../../components/Meta";
import { getSafeUserMessage } from "../../../utils/errorMessages";

import "./AdminPickingSlipScreen.css";

function safe(value = "") {
  return String(value || "").trim();
}

function normalizeLower(value = "") {
  return safe(value).toLowerCase();
}

function humanize(value = "", fallback = "—") {
  const text = safe(value);

  if (!text) return fallback;

  return text
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatDate(value = "") {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function getOrderPayload(state = {}) {
  return state.order || state.details?.order || null;
}

function getPrimaryAddress(order = {}) {
  return (
    order.fulfillment?.shippingAddress ||
    order.shippingAddress ||
    order.billingAddress ||
    {}
  );
}

function getFulfilmentLabel(order = {}) {
  const method = normalizeLower(order.fulfillment?.method || "");

  if (method === "pickup") return "Pickup";

  if (method === "delivery") return "Delivery";

  return "Fulfilment";
}

function getLineId(item = {}) {
  return safe(item.id || item._id || item.lineId || item.sourceCartItemId);
}

function getLineVariant(item = {}) {
  return [
    item.variantName,
    item.variantSku,
    item.variantType,
    item.color,
    item.size,
    item.length,
  ]
    .filter(Boolean)
    .join(" · ");
}

function isTextileLine(item = {}) {
  return (
    normalizeLower(item.productType) === "textile" ||
    normalizeLower(item.itemType) === "textile" ||
    (safe(item.cutStatus) &&
      normalizeLower(item.cutStatus) !== "not_applicable")
  );
}

function getSku(item = {}) {
  return safe(item.variantSku || item.sku) || "—";
}

function getUnitCopy(item = {}) {
  const parts = [
    item.unitLabel,
    item.unitValue ? `${item.unitValue}` : "",
    item.totalLength ? `${item.totalLength}m total` : "",
    item.itemsPerPack ? `${item.itemsPerPack}/pack` : "",
  ].filter(Boolean);

  return parts.join(" · ") || "—";
}

function getLineImage(item = {}) {
  return safe(item.image || item.productImage || item.defaultImage);
}

function ProductCell({ item, fallback }) {
  const image = getLineImage(item);

  return (
    <div className="admin-picking-slip__line-product">
      {image ? (
        <img src={image} alt="" className="admin-picking-slip__line-image" />
      ) : (
        <span className="admin-picking-slip__line-image-placeholder">
          IMAGE
        </span>
      )}

      <div>
        <strong>{item.productName}</strong>
        <span>{getLineVariant(item) || fallback}</span>
      </div>
    </div>
  );
}

function AddressBlock({ order }) {
  const address = getPrimaryAddress(order);

  const lines = [
    address.fullName || order.customer?.name,
    address.company,
    address.line1,
    address.line2,
    address.city,
    address.state,
    address.postalCode,
    address.country,
    address.phone,
  ].filter(Boolean);

  return (
    <section className="admin-picking-slip__address-card">
      <h2>{getFulfilmentLabel(order)} details</h2>

      {lines.length > 0 ? (
        lines.map((line) => <p key={line}>{line}</p>)
      ) : (
        <p>—</p>
      )}

      {order.fulfillment?.pickup?.date ||
      order.fulfillment?.pickup?.timeSlot ||
      order.fulfillment?.pickup?.reference ? (
        <div className="admin-picking-slip__pickup-box">
          <strong>Pickup</strong>
          <span>
            {[
              order.fulfillment?.pickup?.date,
              order.fulfillment?.pickup?.timeSlot,
            ]
              .filter(Boolean)
              .join(" · ") || "Date/time not set"}
          </span>
          <span>{order.fulfillment?.pickup?.reference || ""}</span>
        </div>
      ) : null}
    </section>
  );
}

function CheckCell() {
  return (
    <td className="admin-picking-slip__check-cell">
      <span aria-hidden="true" />
    </td>
  );
}

export default function AdminPickingSlipScreen() {
  const { orderNumber = "" } = useParams();

  const dispatch = useDispatch();

  const adminOrderState = useSelector(
    (state) => state.adminOrder || {},
    shallowEqual,
  );

  const { loading = false, error = null, pending = {} } = adminOrderState;

  const order = useMemo(
    () => getOrderPayload(adminOrderState),
    [adminOrderState],
  );

  const busy = Boolean(loading || pending.details);

  const loadOrder = useCallback(async () => {
    if (!orderNumber) return;

    try {
      await dispatch(getAdminOrderByNumber(orderNumber));
    } catch (err) {
      toast.error(
        getSafeUserMessage(
          err?.message,
          "We could not load this order right now. Please try again later.",
        ),
      );
    }
  }, [dispatch, orderNumber]);

  useEffect(() => {
    loadOrder();

    return () => {
      dispatch(resetAdminOrderError());
      dispatch(resetAdminOrderMeta());
    };
  }, [dispatch, loadOrder]);

  const items = (order?.items || []).map((item) => {
    const qty = Math.max(0, Number(item.qty || 0) - Number(item.statutoryCancelledQty || 0));
    return { ...item, qty, totalLength: item.totalLength ? Number(item.unitValue || 0) * qty : 0 };
  }).filter((item) => item.qty > 0);
  const textileItems = items.filter(isTextileLine);
  const standardItems = items.filter((item) => !isTextileLine(item));

  return (
    <main className="admin-picking-slip">
      <Meta
        title={`Picking Slip ${orderNumber} | Academy Costumes London`}
        description="Printable picking slip for Academy Costumes London studio operations."
        robots="noindex, nofollow"
      />

      <section className="admin-picking-slip__toolbar">
        <Link
          to={`/admin/orders/${encodeURIComponent(orderNumber)}`}
          className="admin-picking-slip__back"
        >
          <ArrowLeft size={16} />
          Back to order
        </Link>

        <div className="admin-picking-slip__toolbar-actions">
          <button
            type="button"
            className="admin-picking-slip__ghost-btn"
            onClick={loadOrder}
            disabled={busy}
          >
            {busy ? (
              <Loader2 size={16} className="is-spinning" />
            ) : (
              <RefreshCw size={16} />
            )}
            Refresh
          </button>

          <button
            type="button"
            className="admin-picking-slip__dark-btn"
            onClick={() => window.print()}
            disabled={!order}
          >
            <Printer size={16} />
            Print
          </button>
        </div>
      </section>

      {error ? <Message variant="danger">{error}</Message> : null}

      {loading && !order ? (
        <div className="admin-picking-slip__loader">
          <Loader />
        </div>
      ) : null}

      {!loading && !order ? (
        <section className="admin-picking-slip__empty">
          <PackageCheck size={38} />

          <h1>Order not found</h1>

          <p>This picking slip could not be loaded.</p>

          <Link to="/admin/orders" className="admin-picking-slip__dark-btn">
            Return to orders
          </Link>
        </section>
      ) : null}

      {order ? (
        <article className="admin-picking-slip__sheet">
          <header className="admin-picking-slip__header">
            <div>
              <div className="admin-picking-slip__brand">
                <span>ACADEMY</span>
                <small>LONDON</small>
              </div>

              <p>Studio picking slip</p>
            </div>

            <div className="admin-picking-slip__meta">
              <h1>Picking Slip</h1>
              <strong>{order.orderNumber || orderNumber}</strong>
              <span>{formatDate(order.createdAt || order.placedAt)}</span>
            </div>
          </header>

          <section className="admin-picking-slip__summary">
            <div>
              <span>Customer</span>
              <strong>{order.customer?.name || "—"}</strong>
              <small>{order.customer?.email || "—"}</small>
            </div>

            <div>
              <span>Fulfilment</span>
              <strong>{getFulfilmentLabel(order)}</strong>
              <small>{humanize(order.fulfillment?.status || "pending")}</small>
            </div>

            <div>
              <span>Items</span>
              <strong>{Number(order.itemCount || items.length || 0)}</strong>
              <small>
                {Number(order.distinctItemCount || items.length || 0)} lines
              </small>
            </div>

            <div>
              <span>Textiles</span>
              <strong>{textileItems.length}</strong>
              <small>Cut/check lines</small>
            </div>
          </section>

          <section className="admin-picking-slip__top-grid">
            <AddressBlock order={order} />

            <section className="admin-picking-slip__address-card">
              <h2>Operational notes</h2>

              <p>
                <strong>Order status:</strong> {humanize(order.status)}
              </p>

              <p>
                <strong>Payment:</strong>{" "}
                {humanize(order.payment?.status || "not_started")}
              </p>

              <p>
                <strong>Delivery status:</strong>{" "}
                {humanize(order.fulfillment?.delivery?.status || "—")}
              </p>

              {order.fulfillment?.delivery?.carrier ||
              order.fulfillment?.delivery?.trackingNumber ? (
                <p>
                  <strong>Courier:</strong>{" "}
                  {[
                    order.fulfillment?.delivery?.carrier,
                    order.fulfillment?.delivery?.service,
                    order.fulfillment?.delivery?.trackingNumber,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              ) : null}

              {order.adminNotes || order.notes ? (
                <div className="admin-picking-slip__note-box">
                  {order.adminNotes || order.notes}
                </div>
              ) : null}
            </section>
          </section>

          {standardItems.length > 0 ? (
            <section className="admin-picking-slip__section">
              <div className="admin-picking-slip__section-title">
                <CheckSquare size={18} />
                <h2>Pick items</h2>
              </div>

              <div className="admin-picking-slip__table-wrap">
                <table className="admin-picking-slip__table">
                  <thead>
                    <tr>
                      <th>Picked</th>
                      <th>Item</th>
                      <th>SKU</th>
                      <th>Qty</th>
                      <th>Unit / pack</th>
                      <th>Location</th>
                      <th>Checked</th>
                    </tr>
                  </thead>

                  <tbody>
                    {standardItems.map((item) => (
                      <tr key={getLineId(item) || item.productName}>
                        <CheckCell />

                        <td>
                          <ProductCell item={item} fallback="Standard item" />
                        </td>

                        <td>{getSku(item)}</td>

                        <td className="admin-picking-slip__qty">
                          {item.qty || 0}
                        </td>

                        <td>{getUnitCopy(item)}</td>

                        <td className="admin-picking-slip__write-cell" />

                        <CheckCell />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}

          {textileItems.length > 0 ? (
            <section className="admin-picking-slip__section">
              <div className="admin-picking-slip__section-title">
                <Scissors size={18} />
                <h2>Textile cutting</h2>
              </div>

              <div className="admin-picking-slip__table-wrap">
                <table className="admin-picking-slip__table">
                  <thead>
                    <tr>
                      <th>Picked</th>
                      <th>Fabric / textile</th>
                      <th>SKU</th>
                      <th>Qty</th>
                      <th>Length / unit</th>
                      <th>Cut status</th>
                      <th>Cut by</th>
                      <th>Checked</th>
                    </tr>
                  </thead>

                  <tbody>
                    {textileItems.map((item) => (
                      <tr key={getLineId(item) || item.productName}>
                        <CheckCell />

                        <td>
                          <ProductCell item={item} fallback="Textile line" />
                        </td>

                        <td>{getSku(item)}</td>

                        <td className="admin-picking-slip__qty">
                          {item.qty || 0}
                        </td>

                        <td>{getUnitCopy(item)}</td>

                        <td>{humanize(item.cutStatus || "pending_cut")}</td>

                        <td className="admin-picking-slip__write-cell" />

                        <CheckCell />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}

          <section className="admin-picking-slip__signoff">
            <div>
              <span>Picked by</span>
            </div>

            <div>
              <span>Checked by</span>
            </div>

            <div>
              <span>Packed by</span>
            </div>

            <div>
              <span>Date / time</span>
            </div>
          </section>

          <footer className="admin-picking-slip__footer">
            <span>Academy Costumes London studio operations</span>
            <span>Generated {formatDate(new Date())}</span>
          </footer>
        </article>
      ) : null}
    </main>
  );
}
